// src/components/resources/quizzes/components/QuizSettings.tsx

import { cn } from '~/lib/utils'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import { FeedbackTiming } from '~/lib/resources/types/tools/quiz'
import type { FeedbackSettings } from '~/lib/resources/types/tools/quiz'

interface QuizSettingsProps {
  feedback: {
    timing: typeof FeedbackTiming[keyof typeof FeedbackTiming]
    settings: FeedbackSettings
  }
  onFeedbackChange: (update: {
    timing?: typeof FeedbackTiming[keyof typeof FeedbackTiming]
    settings?: Partial<FeedbackSettings>
  }) => void
}

export function QuizSettings({ feedback, onFeedbackChange }: QuizSettingsProps): JSX.Element {
  const handleTimingChange = (timing: typeof FeedbackTiming[keyof typeof FeedbackTiming]): void => {
    const isSection = timing === FeedbackTiming.SECTION
    const isEnd = timing === FeedbackTiming.END
    onFeedbackChange({
      timing,
      settings: {
        showVisualIndicators: !isSection,
        showDetailedExplanations: !isSection && !isEnd && feedback.settings.showDetailedExplanations,
        showSectionSummaries: isSection,
        // Preserve other settings
        enableHints: feedback.settings.enableHints
      }
    })
  }

  const handleHintsToggle = (enableHints: boolean): void => {
    onFeedbackChange({
      settings: {
        ...feedback.settings,
        enableHints
      }
    })
  }

  const handleExplanationsToggle = (showDetailedExplanations: boolean): void => {
    onFeedbackChange({
      settings: {
        ...feedback.settings,
        showDetailedExplanations: feedback.timing !== FeedbackTiming.SECTION && showDetailedExplanations
      }
    })
  }

  return (
    <div className="space-y-6">
      {/* Feedback Timing */}
      <div>
        <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          When do you want to see feedback?
        </h3>
        <div className="space-y-2">
          {[
            {
              value: FeedbackTiming.IMMEDIATE,
              label: 'After each question',
              description: 'Get instant feedback as you go'
            },
            {
              value: FeedbackTiming.SECTION,
              label: 'After each section',
              description: 'Review answers section by section'
            },
            {
              value: FeedbackTiming.END,
              label: 'At the end',
              description: 'See all feedback after completing the quiz'
            }
          ].map(option => (
            <label
              key={option.value}
              className={cn(
                "flex cursor-pointer items-center rounded-lg border p-3 transition-colors",
                feedback.timing === option.value
                  ? "border-indigo-600 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-900/30"
                  : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50"
              )}
            >
              <input
                type="radio"
                name="timing"
                value={option.value}
                checked={feedback.timing === option.value}
                onChange={(e) => handleTimingChange(e.target.value as typeof FeedbackTiming[keyof typeof FeedbackTiming])}
                className="sr-only"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors",
                    feedback.timing === option.value
                      ? "border-indigo-600 bg-indigo-600 dark:border-indigo-400 dark:bg-indigo-400"
                      : "border-gray-300 dark:border-gray-600"
                  )}>
                    {feedback.timing === option.value && (
                      <div className="h-1.5 w-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </span>
                </div>
                <p className="ml-7 mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {option.description}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Additional Settings */}
      <div>
        <h3 className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Additional settings
        </h3>
        <div className="space-y-2">
          {[
            {
              id: 'hints',
              checked: feedback.settings.enableHints,
              onChange: handleHintsToggle,
              icon: RiLightbulbLine,
              label: 'Show hints',
              description: 'Get helpful tips when stuck'
            },
            {
              id: 'explanations',
              checked: feedback.settings.showDetailedExplanations,
              onChange: handleExplanationsToggle,
              icon: RiInformationLine,
              label: 'Show explanations',
              description: 'See why answers are correct/incorrect',
              disabled: feedback.timing === FeedbackTiming.SECTION
            }
          ].map(setting => (
            <label
              key={setting.id}
              className={cn(
                "flex cursor-pointer items-center rounded-lg border p-3 transition-colors",
                setting.checked
                  ? "border-indigo-600 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-900/30"
                  : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50",
                setting.disabled && "opacity-50 cursor-not-allowed"
              )}
            >
              <input
                type="checkbox"
                checked={setting.checked}
                onChange={(e) => setting.onChange(e.target.checked)}
                disabled={setting.disabled}
                className="sr-only"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                    setting.checked
                      ? "border-indigo-600 bg-indigo-600 dark:border-indigo-400 dark:bg-indigo-400"
                      : "border-gray-300 dark:border-gray-600"
                  )}>
                    {setting.checked && (
                      <RiCheckLine className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <span className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                    <setting.icon className="h-4 w-4 text-gray-400" />
                    {setting.label}
                  </span>
                </div>
                <p className="ml-7 mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {setting.description}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}